import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore.js';
import { useProjectStore } from '../../stores/projectStore.js';
import { useUserRole } from '../../hooks/useUserRole.js';
import { createLocation, deleteLocation } from '../../services/visionProjectsClient.js';
import { moveProjectImage, bulkMoveProjectImages } from '../../services/visionAssetsClient.js';
import { webDashboardUrl } from '../../utils/webAppLinks.js';
import { visionPath as appHref } from '../../utils/webAppLinks.js';
import { isPlainLeftClick } from './isPlainLeftClick.js';
import {
  IconAdd,
  IconArrowLeft,
  IconGridView,
  IconSettings,
  IconShotList,
  IconStoryboard,
  IconTrash,
  IconVisionBoard,
} from './SidebarIcons.jsx';
import { SidebarConfirm } from './SidebarConfirm.jsx';
import { SidebarRow } from './SidebarRow.jsx';
import { SidebarTooltip } from './SidebarTooltip.jsx';
import { SidebarStyle } from './sidebarStyle.js';

const { Locations } = SidebarStyle;

function initialOf(name) {
  return name.trim().charAt(0).toUpperCase() || '#';
}

export function ProjectNav({
  geometry,
  project,
  projectId,
  categories,
  totalImages,
  activeCategoryId,
  isOnArtboardRoot,
  isSettingsPage,
  isShotlistPage,
  isStoryboardPage,
  isVisionBoardPage,
}) {
  const navigate = useNavigate();
  const rail = geometry.rail;

  const back = (
    <SidebarRow
      geometry={geometry}
      label="Projects"
      href={webDashboardUrl()}
      external
      inset={SidebarStyle.NAV_ROW_INSET}
      height={SidebarStyle.NAV_ROW_HEIGHT}
      iconContent={<IconArrowLeft size={16} />}
      textContent={<span className="vs-label">Projects</span>}
    />
  );

  if (!projectId) return <div className="vs-nav-list">{back}</div>;

  const go = (path) => () => navigate(path);
  const projectPath = `/project/${projectId}`;
  const pageRow = (label, path, selected, Icon) => (
    <SidebarRow
      geometry={geometry}
      label={label}
      selected={selected}
      href={appHref(path)}
      onClick={go(path)}
      inset={SidebarStyle.NAV_ROW_INSET}
      height={SidebarStyle.NAV_ROW_HEIGHT}
      iconContent={<Icon size={SidebarStyle.NAV_ICON_HEIGHT} />}
      textContent={<span className="vs-label">{label}</span>}
    />
  );

  return (
    <div className="vs-nav-list">
      {back}

      {project && (
        <div className={`vs-project vs-reveal${rail ? ' is-hidden' : ''}`} aria-hidden={rail}>
          <span className="vs-project-name" title={project.name}>{project.name}</span>
          <ProjectSettingsGear
            selected={isSettingsPage}
            onClick={go(`${projectPath}/settings`)}
          />
        </div>
      )}

      {pageRow('Shot List', `${projectPath}/shotlist`, isShotlistPage, IconShotList)}
      {pageRow('Storyboard', `${projectPath}/storyboard`, isStoryboardPage, IconStoryboard)}
      {pageRow('Vision Board', `${projectPath}/vision-board`, isVisionBoardPage, IconVisionBoard)}

      <LocationsSection
        geometry={geometry}
        projectId={projectId}
        categories={categories}
        totalImages={totalImages}
        activeCategoryId={activeCategoryId}
        isOnArtboardRoot={isOnArtboardRoot}
      />
    </div>
  );
}

function ProjectSettingsGear({ selected, onClick }) {
  const { hasFullAccess } = useUserRole();
  if (!hasFullAccess) return null;

  return (
    <button
      type="button"
      className={`vs-gear${selected ? ' is-selected' : ''}`}
      aria-label="Project Settings"
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
    >
      <IconSettings size={18} />
    </button>
  );
}

function LocationsSection({ geometry, projectId, categories, totalImages, activeCategoryId, isOnArtboardRoot }) {
  const navigate = useNavigate();
  const { canEdit } = useUserRole();
  const rail = geometry.rail;
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [newLocationPortal, setNewLocationPortal] = useState(null);
  const locationsListRef = useRef(null);
  const addLocationRef = useRef(null);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [railSeen, setRailSeen] = useState(rail);
  if (rail !== railSeen) {
    setRailSeen(rail);
    if (rail) setExpanded(false);
  }
  const artboardPath = `/project/${projectId}/artboard`;

  const openArtboard = (categoryId) => () =>
    navigate(categoryId == null ? artboardPath : `${artboardPath}/${categoryId}`);

  const confirmCategoryDelete = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteLocation(projectId, categoryToDelete.id);
      useProjectStore.getState().removeCategory(categoryToDelete.id);
      toast.success('Category deleted');
      useAuthStore.getState().refreshStorage();
      if (activeCategoryId === categoryToDelete.id) navigate(artboardPath);
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    } finally {
      setCategoryToDelete(null);
    }
  };

  const handleAddLocation = async () => {
    const name = newLocationName.trim();
    if (!name) return;
    try {
      const address = newLocationAddress.trim();
      const { categories: existing } = useProjectStore.getState();
      const nextSortOrder = existing.reduce((max, cat) => Math.max(max, cat.sort_order || 0), 0) + 1;
      const category = await createLocation(projectId, { name, address, sortOrder: nextSortOrder });
      useProjectStore.getState().addCategory(category);
      handleCloseNewLocation();
    } catch (error) {
      console.error('Failed to create location:', error);
    }
  };

  // The form lives in the mobile sheet while the sheet is its host; it goes when the sheet closes
  useEffect(() => {
    if (!showNewLocation || !(newLocationPortal instanceof HTMLDialogElement)) return undefined;
    const onClose = () => {
      setShowNewLocation(false);
      setNewLocationName('');
      setNewLocationAddress('');
    };
    newLocationPortal.addEventListener('close', onClose);
    return () => newLocationPortal.removeEventListener('close', onClose);
  }, [showNewLocation, newLocationPortal]);

  const handleCloseNewLocation = () => {
    setShowNewLocation(false);
    setNewLocationName('');
    setNewLocationAddress('');
  };

  const shownTiles = expanded ? categories : categories.slice(0, Locations.RAIL_TILE_COUNT);
  const hiddenTiles = categories.length - shownTiles.length;

  return (
    <>
      <div style={{ height: 8, flex: 'none' }} />

      <div className={`vs-section-label vs-reveal${rail ? ' is-hidden' : ''}`} aria-hidden={rail}>
        Locations
        {canEdit && (
          <button
            type="button"
            ref={addLocationRef}
            className="vs-add"
            aria-label="Add location"
            onClick={(event) => {
              event.stopPropagation();
              setNewLocationPortal(event.currentTarget.closest('dialog') ?? document.body);
              setShowNewLocation(true);
            }}
          >
            <IconAdd size={12} />
          </button>
        )}
      </div>

      <SidebarRow
        geometry={geometry}
        label="Artboard"
        selected={isOnArtboardRoot}
        href={appHref(artboardPath)}
        onClick={openArtboard(null)}
        inset={SidebarStyle.NAV_ROW_INSET}
        height={SidebarStyle.NAV_ROW_HEIGHT}
        iconContent={<IconGridView size={SidebarStyle.NAV_ICON_HEIGHT} />}
        textContent={
          <>
            <span className="vs-label">Artboard</span>
            <span className="vs-count" style={isOnArtboardRoot ? { color: 'inherit', opacity: 0.7 } : undefined}>
              {totalImages}
            </span>
          </>
        }
      />

      <div className="vs-locations">
        <div
          ref={locationsListRef}
          className={`vs-locations-list vs-reveal${rail ? ' is-hidden' : ''}`}
          role="list"
          aria-label="Locations"
          aria-hidden={rail}
        >
          {categories.length === 0 ? (
            <span className="vs-muted">No locations yet</span>
          ) : (
            categories.map((category) => (
              <LocationRow
                key={category.id}
                category={category}
                projectId={projectId}
                active={activeCategoryId === category.id}
                canEdit={canEdit}
                href={appHref(`${artboardPath}/${category.id}`)}
                onOpen={openArtboard(category.id)}
                onDelete={() => setCategoryToDelete(category)}
              />
            ))
          )}
        </div>

        <div
          className={`vs-rail-stack vs-reveal${rail ? '' : ' is-hidden'}`}
          role="list"
          aria-label="Locations"
          aria-hidden={!rail}
        >
          {shownTiles.map((category) => (
            <div key={category.id} role="listitem">
              <SidebarTooltip text={category.name} enabled={rail}>
                <a
                  className={`vs-tile${activeCategoryId === category.id ? ' is-active' : ''}`}
                  href={appHref(`${artboardPath}/${category.id}`)}
                  aria-label={category.name}
                  data-focus-key={`location:${category.id}`}
                  onClick={(event) => {
                    if (!isPlainLeftClick(event)) return;
                    event.preventDefault();
                    openArtboard(category.id)();
                  }}
                >
                  <span className="vs-tile-face">{initialOf(category.name)}</span>
                </a>
              </SidebarTooltip>
            </div>
          ))}
          {hiddenTiles > 0 && (
            <button
              type="button"
              className="vs-more"
              aria-label={`Show ${hiddenTiles} more`}
              onClick={() => setExpanded(true)}
            >
              +{hiddenTiles}
            </button>
          )}
        </div>
      </div>

      {showNewLocation && createPortal(
        <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCloseNewLocation} />
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              event.preventDefault();
              handleCloseNewLocation();
              addLocationRef.current?.focus();
            }}
          >
            <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md" onClick={(event) => event.stopPropagation()}>
              <div className="px-6 pt-6 pb-4">
                <h3 className="text-lg font-semibold text-text-primary mb-4">New Location</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Location Name *</label>
                    <input
                      type="text"
                      value={newLocationName}
                      onChange={(event) => setNewLocationName(event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter') handleAddLocation(); }}
                      placeholder="e.g. Warehouse Interior"
                      maxLength={50}
                      autoFocus
                      className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-text-primary placeholder-text-tertiary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Address</label>
                    <input
                      type="text"
                      value={newLocationAddress}
                      onChange={(event) => setNewLocationAddress(event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter') handleAddLocation(); }}
                      placeholder="e.g. 123 Main St, Los Angeles, CA"
                      className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2 text-text-primary placeholder-text-tertiary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
                <button onClick={handleCloseNewLocation} className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary rounded-xl hover:bg-surface-elevated transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleAddLocation}
                  disabled={!newLocationName.trim()}
                  className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Location
                </button>
              </div>
            </div>
          </div>
        </>,
        newLocationPortal ?? document.body,
      )}

      {categoryToDelete && createPortal(
        <SidebarConfirm
          text={`This will permanently delete "${categoryToDelete.name}" and all ${categoryToDelete.image_count || 0} image${(categoryToDelete.image_count || 0) === 1 ? '' : 's'} in it. This action cannot be undone.`}
          confirmText="Delete Category"
          onClose={() => {
            locationsListRef.current
              ?.querySelector(`[data-focus-key="location:${CSS.escape(categoryToDelete.id)}"]`)
              ?.focus();
            setCategoryToDelete(null);
          }}
          onConfirm={confirmCategoryDelete}
        />,
        document.body,
      )}
    </>
  );
}

const LONG_PRESS_MILLIS = 500;

function LocationRow({ category, projectId, active, canEdit, href, onOpen, onDelete }) {
  const [dragOver, setDragOver] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focusedWithin, setFocusedWithin] = useState(false);
  const [longPressed, setLongPressed] = useState(false);
  const longPressTimer = useRef(0);
  const touchMoved = useRef(false);
  const itemRef = useRef(null);
  const showDelete = canEdit && (hovered || focusedWithin || longPressed) && !dragOver;

  useEffect(() => () => window.clearTimeout(longPressTimer.current), []);

  useEffect(() => {
    if (!longPressed) return undefined;
    const item = itemRef.current;
    const onPointerDown = (event) => {
      if (!item?.contains(event.target)) setLongPressed(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [longPressed]);

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = event.dataTransfer.types.includes('Files') ? 'copy' : 'move';
    setDragOver(true);
  };

  const onDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(false);
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setDragOver(false);

    if (event.dataTransfer.files?.length > 0 && event.dataTransfer.types.includes('Files')) {
      window.dispatchEvent(new CustomEvent('sidebar-file-drop', {
        detail: { files: event.dataTransfer.files, categoryId: category.id },
      }));
      return;
    }

    const imageId = event.dataTransfer.getData('imageId');
    const sourceCategoryId = event.dataTransfer.getData('categoryId');
    const selectedIdsRaw = event.dataTransfer.getData('selectedImageIds');
    if (!imageId || sourceCategoryId === String(category.id)) return;

    try {
      if (selectedIdsRaw) {
        const selectedIds = JSON.parse(selectedIdsRaw);
        if (selectedIds.length > 1) {
          await bulkMoveProjectImages({ projectId, imageIds: selectedIds, locationId: category.id });
          window.dispatchEvent(new CustomEvent('sidebar-image-moved'));
          return;
        }
      }
      await moveProjectImage({ projectId, imageId, locationId: category.id });
      window.dispatchEvent(new CustomEvent('sidebar-image-moved'));
    } catch (error) {
      console.error('Failed to move image(s) to category:', error);
    }
  };

  return (
    <div
      ref={itemRef}
      role="listitem"
      className="vs-loc-item"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={() => setHovered(false)}
      onFocus={(event) => { if (event.target.matches(':focus-visible')) setFocusedWithin(true); }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusedWithin(false);
      }}
      onTouchStart={() => {
        touchMoved.current = false;
        window.clearTimeout(longPressTimer.current);
        longPressTimer.current = window.setTimeout(() => {
          if (!touchMoved.current) setLongPressed(true);
        }, LONG_PRESS_MILLIS);
      }}
      onTouchMove={() => { touchMoved.current = true; window.clearTimeout(longPressTimer.current); }}
      onTouchEnd={() => window.clearTimeout(longPressTimer.current)}
    >
      <a
        href={href}
        title={category.name}
        data-focus-key={`location:${category.id}`}
        className={`vs-loc${dragOver ? ' is-drop' : active ? ' is-active' : ''}`}
        onClick={(event) => {
          if (!isPlainLeftClick(event)) return;
          event.preventDefault();
          if (longPressed) { setLongPressed(false); return; }
          onOpen();
        }}
      >
        <span className="vs-dot" />
        <span className="vs-loc-name">{category.name}</span>
        {showDelete ? (
          <span className="vs-loc-delete-space" />
        ) : (
          <span className="vs-count" style={active ? { color: 'inherit', opacity: 0.7 } : undefined}>
            {category.image_count}
          </span>
        )}
      </a>
      {showDelete && (
        <button
          type="button"
          className="vs-loc-delete"
          aria-label="Delete category"
          onClick={() => { setLongPressed(false); onDelete(); }}
        >
          <IconTrash size={14} />
        </button>
      )}
    </div>
  );
}
